import React, { useState, useEffect } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import "./MemoryGame.css";

import cardBack from "./images/5.png";
import image1 from "./images/1.png";
import image2 from "./images/2.png";
import image3 from "./images/3.png";
import image4 from "./images/4.png";
import image6 from "./images/6.png";
import image7 from "./images/7.png";
import image8 from "./images/8.png";
import image9 from "./images/9.png";

function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// Erzeugt das Kartenset dynamisch basierend auf dem aktuellen Level.
// Für Level 1: 2 Paare, Level 2: 3 Paare, ... bis maximal 8 Paare.
function getCardSet(level) {
  const images = [image1, image2, image3, image4, image6, image7, image8, image9];
  const numPairs = Math.min(2 + level - 1, images.length);
  const selectedImages = images.slice(0, numPairs);
  let cards = [];
  selectedImages.forEach((img, index) => {
    cards.push({
      id: index * 2,
      img: img,
      flipped: false,
      matched: false,
    });
    cards.push({
      id: index * 2 + 1,
      img: img,
      flipped: false,
      matched: false,
    });
  });
  return shuffle(cards);
}

function MemoryGame() {
  const initialTime = 60; // Startzeit für Level 1 in Sekunden
  const [level, setLevel] = useState(1);
  const [cards, setCards] = useState(getCardSet(1));
  const [firstCard, setFirstCard] = useState(null);
  const [disableClicks, setDisableClicks] = useState(false);
  const [timer, setTimer] = useState(0);
  const [maxTime, setMaxTime] = useState(initialTime);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [ranking, setRanking] = useState([]);
  const [showRanking, setShowRanking] = useState(false);

  // Die Flip-Verzögerung sinkt mit steigendem Level, mindestens 300ms
  const flipDelay = Math.max(300, 1000 - (level - 1) * 37);

  // Timer-Effekt
  useEffect(() => {
    if (!gameOver) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev + 1 >= maxTime) {
            setGameOver(true);
            return maxTime;
          }
          return prev + 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameOver, maxTime]);

  // Beim Spielende: Ranking speichern und abrufen
  useEffect(() => {
    if (gameOver) {
      const username = localStorage.getItem("username") || "Unknown";
      axios
        .post("/api/storeRanking", { username, time: timer, level })
        .catch((error) =>
          console.error("Fehler beim Speichern der Rangliste:", error)
        );
      if (!won) {
        axios
          .get("/api/ranking")
          .then((response) => setRanking(response.data))
          .catch((error) =>
            console.error("Fehler beim Abrufen der Rangliste:", error)
          );
      }
    }
  }, [gameOver, won, timer, level]);

  const handleCardClick = (card) => {
    if (disableClicks || card.flipped || card.matched || gameOver) return;
    const newCards = cards.map((c) =>
      c.id === card.id ? { ...c, flipped: true } : c
    );
    setCards(newCards);
    if (!firstCard) {
      setFirstCard(card);
    } else {
      setDisableClicks(true);
      if (firstCard.img === card.img) {
        const updatedCards = newCards.map((c) =>
          c.img === card.img ? { ...c, matched: true } : c
        );
        setCards(updatedCards);
        setFirstCard(null);
        setDisableClicks(false);
        if (updatedCards.every((c) => c.flipped || c.matched)) {
          setGameOver(true);
          setWon(true);
          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
          });
        }
      } else {
        setTimeout(() => {
          const revertedCards = newCards.map((c) =>
            c.id === firstCard.id || c.id === card.id
              ? { ...c, flipped: false }
              : c
          );
          setCards(revertedCards);
          setFirstCard(null);
          setDisableClicks(false);
        }, flipDelay);
      }
    }
  };

  const resetGame = () => {
    setCards(getCardSet(level));
    setFirstCard(null);
    setDisableClicks(false);
    setTimer(0);
    setGameOver(false);
    setWon(false);
    setRanking([]);
    setShowRanking(false);
  };

  const nextLevel = () => {
    const newLevel = level + 1;
    const newMaxTime = Math.max(15, maxTime - 5);
    setLevel(newLevel);
    setMaxTime(newMaxTime);
    resetGame();
  };

  const fetchRanking = async () => {
    try {
      const response = await axios.get("/api/ranking");
      setRanking(response.data);
      setShowRanking(true);
    } catch (error) {
      console.error("Fehler beim Abrufen der Rangliste:", error);
    }
  };

  const closeRanking = () => {
    setShowRanking(false);
  };

  return (
    <div className="memory-container">
      <div className="fixed-header">
        <h1 className="memory-title">Memory Game</h1>
        <div className="level-display">Level: {level}</div>
        <div className="timer">Zeit: {timer}s</div>
      </div>
      <div className="memory-grid">
        {cards.map((card) => (
          <div
            key={card.id}
            className={`card ${card.flipped || card.matched ? "flipped" : ""}`}
            onClick={() => handleCardClick(card)}
          >
            <div className="card-inner">
              <div className="card-front">
                <img src={card.img} alt="Vorderseite" />
              </div>
              <div className="card-back">
                <img src={cardBack} alt="Rückseite" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="footer-buttons">
        <button className="reset-button" onClick={resetGame}>
          Neustarten
        </button>
        <button className="endsurvey-button" onClick={() => (window.location.href = "/endsurvey")}>
          Endumfrage
        </button>
      </div>
      {gameOver && (
        <div className="extra-buttons">
          {won && level < 20 && (
            <button className="next-level-button" onClick={nextLevel}>
              Nächstes Level
            </button>
          )}
          <button className="ranking-button" onClick={fetchRanking}>
            Rangliste
          </button>
        </div>
      )}
      {gameOver && showRanking && ranking.length > 0 && (
        <div className="ranking-container">
          <button className="close-ranking" onClick={closeRanking}>
            X
          </button>
          <h2>Rangliste</h2>
          <ul className="ranking-list">
            {ranking.map((entry, index) => (
              <li key={index}>
                {index === 0 && "🥇 "}
                {index === 1 && "🥈 "}
                {index === 2 && "🥉 "}
                {index + 1}. {entry.username} – Level {entry.highestLevel} in {entry.bestTime}s
              </li>
            ))}
          </ul>
        </div>
      )}
      {gameOver && showRanking && ranking.length === 0 && (
        <div className="ranking-container">
          <button className="close-ranking" onClick={closeRanking}>
            X
          </button>
          <p>Rangliste wird geladen...</p>
        </div>
      )}
    </div>
  );
}

export default MemoryGame;